// SPDX-License-Identifier: MIT

// Consent persistence — a single first-party cookie holding the JSON record
// { hasConsented, choices: { [id]: boolean } }, stored under the namespace name.
//
// A cookie (not localStorage) so the record can be read server-side / at the
// edge and, with the `domain` option, shared across subdomains. The consent
// record is itself a "strictly necessary" cookie, so storing it needs no consent.
// Reads/writes are guarded: if cookies are unavailable, choices simply don't
// persist rather than throwing.

const DEFAULT_MAX_AGE_DAYS = 365;

const canUseCookies = () => typeof document !== 'undefined';

const isSecure = () =>
  typeof window !== 'undefined' &&
  window.location &&
  window.location.protocol === 'https:';

// Reads the namespace cookie into { hasConsented, choices, source }. `source` is
// 'explicit' (a banner/preferences action) or 'implied' (granted out of a geo
// zone); null on a pre-geo cookie that predates source tracking.
const readRecord = (namespace) => {
  const empty = { hasConsented: false, choices: {}, source: null };
  if (!canUseCookies()) return empty;
  try {
    const prefix = `${namespace}=`;
    const entry = document.cookie
      .split('; ')
      .find((c) => c.indexOf(prefix) === 0);
    if (!entry) return empty;
    const parsed = JSON.parse(decodeURIComponent(entry.slice(prefix.length)));
    return {
      hasConsented: !!parsed.hasConsented,
      choices: parsed.choices && typeof parsed.choices === 'object' ? parsed.choices : {},
      source: typeof parsed.source === 'string' ? parsed.source : null,
    };
  } catch (e) {
    return empty;
  }
};

const cookieString = (namespace, value, options, withDomain) => {
  const maxAge = (options.maxAgeDays || DEFAULT_MAX_AGE_DAYS) * 24 * 60 * 60;
  const parts = [`${namespace}=${value}`, 'path=/', `max-age=${maxAge}`, 'SameSite=Lax'];
  if (withDomain && options.domain) parts.push(`domain=${options.domain}`);
  if (isSecure()) parts.push('Secure');
  return parts.join('; ');
};

// True if a cookie named `namespace` currently holds exactly `value`.
const cookieHolds = (namespace, value) => {
  const prefix = `${namespace}=`;
  return document.cookie
    .split('; ')
    .some((c) => c.indexOf(prefix) === 0 && c.slice(prefix.length) === value);
};

const writeRecord = (namespace, record, options = {}) => {
  if (!canUseCookies()) return;
  try {
    const value = encodeURIComponent(JSON.stringify(record));
    document.cookie = cookieString(namespace, value, options, true);
    // A `domain` cookie is silently rejected when the current host isn't under
    // that domain (e.g. previewing on a Netlify/Gatsby Cloud URL instead of the
    // real site). Detect the rejection by reading it back and fall back to a
    // host-only cookie so consent still persists off the production domain.
    if (options.domain && !cookieHolds(namespace, value)) {
      document.cookie = cookieString(namespace, value, options, false);
    }
  } catch (e) {
    /* no-op: consent just won't persist */
  }
};

// The stored choice for one consent type: true/false, or null if never chosen.
const getChoice = (namespace, id) => {
  const { choices } = readRecord(namespace);
  return id in choices ? !!choices[id] : null;
};

const setChoice = (namespace, id, accepted, options) => {
  const record = readRecord(namespace);
  record.choices[id] = !!accepted;
  writeRecord(namespace, record, options);
};

// Whether the visitor has made an initial choice (any banner/modal action).
const hasConsented = (namespace) => readRecord(namespace).hasConsented;

// How the current consent was set: 'explicit', 'implied', or null (see readRecord).
const consentSource = (namespace) => readRecord(namespace).source;

const setConsented = (namespace, options = {}, source = 'explicit') => {
  const record = readRecord(namespace);
  record.hasConsented = true;
  record.source = source;
  writeRecord(namespace, record, options);
};

const clearAll = (namespace, options = {}) => {
  if (!canUseCookies()) return;
  const base = [`${namespace}=`, 'path=/', 'max-age=0'];
  // Clear both the shared-domain and host-only variants (writeRecord may have
  // written either — see the fallback above).
  document.cookie = base.join('; ');
  if (options.domain) document.cookie = [...base, `domain=${options.domain}`].join('; ');
};

// States for the preferences UI checkboxes: stored choice, else the type's
// defaultValue, with required types always on.
const displayStates = (namespace, consentTypes) => {
  const { choices } = readRecord(namespace);
  const states = {};
  consentTypes.forEach((type) => {
    if (type.required) {
      states[type.id] = true;
      return;
    }
    states[type.id] = type.id in choices ? !!choices[type.id] : !!type.defaultValue;
  });
  return states;
};

// States to actually enforce on load. Required types are granted; optional types
// are only granted once the visitor has consented (otherwise denied), so nothing
// optional runs before an explicit choice.
const appliedStates = (namespace, consentTypes) => {
  const { hasConsented: consented, choices } = readRecord(namespace);
  const states = {};
  consentTypes.forEach((type) => {
    if (type.required) {
      states[type.id] = true;
    } else if (!consented) {
      states[type.id] = false;
    } else {
      states[type.id] = type.id in choices ? !!choices[type.id] : !!type.defaultValue;
    }
  });
  return states;
};

module.exports = {
  getChoice,
  setChoice,
  hasConsented,
  consentSource,
  setConsented,
  clearAll,
  displayStates,
  appliedStates,
};
